import React, { useState, useEffect } from 'react';
import IconRenderer from '../../ui/IconRenderer';
import { db } from '../../../firebase/firebaseConfig';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import './ExplorePreview.css';

const ExplorePreview = ({
  template,
  language,
  onBack,
  onViewDetails,
  isTemplateOwned
}) => {
  const [previewItems, setPreviewItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    loadPreviewItems();
  }, [template]);

  const loadPreviewItems = async () => {
    if (!template || !template.contentId || !template.isCatalogItem) {
      console.warn('ExplorePreview: Missing required template data', {
        hasTemplate: !!template,
        hasContentId: !!(template?.contentId),
        isCatalogItem: !!(template?.isCatalogItem)
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const contentId = template.contentId;
      let items = [];

      if (template.type === 'course') {
        // Parse contentId: contentId is just the course ID (e.g., "abc123"), not "courses/abc123"
        const courseId = contentId.startsWith('courses/')
          ? contentId.replace('courses/', '')
          : contentId;

        console.log('Loading course preview:', { courseId, contentId, templateType: template.type });

        const courseRef = doc(db, 'courses', courseId);
        const courseDoc = await getDoc(courseRef);

        if (!courseDoc.exists()) {
          console.warn('Course document does not exist:', courseId);
          setPreviewItems([]);
          setIsLoading(false);
          return;
        }

        const courseData = courseDoc.data();
        const syllabus = courseData.syllabus || [];

        console.log('Course data loaded:', {
          hasSyllabus: !!syllabus,
          syllabusLength: syllabus.length,
          courseTitle: courseData.title
        });

        // Extract snippets from lessons and subLessons
        syllabus.forEach((lesson, lessonIndex) => {
          if (lesson.subLessons && Array.isArray(lesson.subLessons)) {
            lesson.subLessons.forEach((subLesson, subLessonIndex) => {
              // Check for codeSnippet in various possible fields
              const codeSnippet = subLesson.codeSnippet || subLesson.code || subLesson.solution || '';
              if (codeSnippet && codeSnippet.trim()) {
                items.push({
                  id: subLesson.id || `${lesson.id || lessonIndex}-${subLesson.title || subLessonIndex}`,
                  title: subLesson.title || 'Untitled',
                  description: subLesson.description || '',
                  code: codeSnippet,
                  lessonTitle: lesson.title || '',
                  type: 'snippet'
                });
              }
            });
          }
        });

        console.log(`Extracted ${items.length} snippets from course`);
      } else if (template.type === 'cookbook') {
        // Parse contentId: contentId is just the cookbook ID (e.g., "abc123"), not "cookbooks/abc123"
        const cookbookId = contentId.startsWith('cookbooks/')
          ? contentId.replace('cookbooks/', '')
          : contentId;

        console.log('Loading cookbook preview:', { cookbookId, contentId, templateType: template.type });

        const cardsRef = collection(db, `cookbooks/${cookbookId}/cards`);
        const cardsSnapshot = await getDocs(cardsRef);

        cardsSnapshot.docs.forEach(cardDoc => {
          const cardData = cardDoc.data();
          const code = cardData.solution || cardData.code || cardData.codeSnippet || '';
          if (code && code.trim()) {
            items.push({
              id: cardDoc.id,
              title: cardData.title || 'Untitled',
              description: cardData.description || '',
              code: code,
              type: 'card'
            });
          }
        });

        console.log(`Extracted ${items.length} cards from cookbook`);
      }

      setPreviewItems(items);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error loading preview items:', error);
      console.error('Error details:', {
        template: template ? {
          id: template.id,
          contentId: template.contentId,
          type: template.type,
          isCatalogItem: template.isCatalogItem
        } : null,
        errorMessage: error.message,
        errorStack: error.stack
      });
      setPreviewItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + previewItems.length) % previewItems.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % previewItems.length);
  };

  const currentItem = previewItems[currentIndex];

  return (
    <div className="explore-preview-container">
      <div className="explore-preview-header">
        <div className="explore-preview-header-left">
          {/* <button
            type="button"
            className="explore-preview-back-btn"
            onClick={onBack}
            aria-label="Back to details"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span>Back</span>
          </button> */}
          <div className="explore-preview-title-section">
            <IconRenderer
              name={language.toLowerCase()}
              type="language"
              size="small"
              className="explore-preview-language-icon"
            />
            <div>
              <h2 className="explore-preview-title">{template?.title || 'Preview'}</h2>
              <p className="explore-preview-subtitle">
                {previewItems.length} {template?.type === 'course' ? 'snippets' : 'cards'} available
              </p>
            </div>
          </div>
        </div>
        <div className="explore-preview-header-right">
          {previewItems.length > 0 && (
            <div className="explore-preview-counter">
              {currentIndex + 1} / {previewItems.length}
            </div>
          )}
          {onViewDetails && (
            <button
              type="button"
              className={`explore-grid-card-action-icon explore-grid-card-action-icon--details ${isTemplateOwned && isTemplateOwned(template) ? 'explore-grid-card-action-icon--owned' : ''}`}
              onClick={onViewDetails}
              aria-label="View Details"
              title="View Details"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="explore-preview-content">
        {isLoading ? (
          <div className="explore-preview-loading">
            <div className="spinner"></div>
            <p>Loading preview...</p>
          </div>
        ) : previewItems.length === 0 ? (
          <div className="explore-preview-empty">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.5, marginBottom: '16px' }}>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <p>No {template?.type === 'course' ? 'snippets' : 'cards'} available for preview</p>
            <p className="explore-preview-empty-subtitle">
              This {template?.type === 'course' ? 'course' : 'cookbook'} doesn't have any previewable content yet.
            </p>
          </div>
        ) : currentItem ? (
          <div className="explore-preview-item">
            <div className="explore-preview-item-header">
              <h3 className="explore-preview-item-title">{currentItem.title}</h3>
              {currentItem.lessonTitle && (
                <span className="explore-preview-item-lesson">{currentItem.lessonTitle}</span>
              )}
            </div>
            {currentItem.description && (
              <p className="explore-preview-item-description">{currentItem.description}</p>
            )}
            {currentItem.code && (
              <div className="explore-preview-item-code">
                <pre><code>{currentItem.code}</code></pre>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {previewItems.length > 0 && (
        <div className="explore-preview-navigation">
          <button
            type="button"
            className="explore-preview-nav-btn explore-preview-nav-btn--prev"
            onClick={handlePrevious}
            disabled={isLoading || previewItems.length === 0}
            aria-label="Previous item"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            <span>Previous</span>
          </button>
          <div className="explore-preview-dots">
            {previewItems.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`explore-preview-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to item ${index + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="explore-preview-nav-btn explore-preview-nav-btn--next"
            onClick={handleNext}
            disabled={isLoading || previewItems.length === 0}
            aria-label="Next item"
          >
            <span>Next</span>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExplorePreview;

